import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { participantesApi, eventosApi, lotesApi } from '../../lib/api';
import { Printer, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import logger from '../../lib/logger';

const ParticipantesImprimirPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [participantes, setParticipantes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [lotes, setLotes] = useState([]);

  const filterStatus = searchParams.get('status') || 'all';
  const filterEvento = searchParams.get('evento') || 'all';
  const filterLote = searchParams.get('lote') || 'all';
  const filterCheckin = searchParams.get('checkin') || 'all';

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, eRes, lRes] = await Promise.all([
          participantesApi.list(),
          eventosApi.list(),
          lotesApi.list(),
        ]);
        setParticipantes(pRes.data || []);
        setEventos(eRes.data || []);
        setLotes(lRes.data || []);
      } catch (err) {
        logger.error('Print fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getEventoName = (id) => eventos.find(e => e.id === id || e.evento_id === id)?.nome_evento || '-';
  const getLoteName = (id) => lotes.find(l => l.id === id || l.lote_id === id)?.nome_lote || '-';

  const filtered = participantes.filter(p => {
    if (filterStatus !== 'all' && p.status_pagamento !== filterStatus) return false;
    if (filterEvento !== 'all' && p.evento_id !== filterEvento) return false;
    if (filterLote !== 'all' && p.lote_id !== filterLote) return false;
    if (filterCheckin === 'presente' && p.status_checkin !== 'realizado' && p.status_checkin !== 'presente') return false;
    if (filterCheckin === 'nao_chegou' && (p.status_checkin === 'realizado' || p.status_checkin === 'presente')) return false;
    return true;
  });

  // Sort by name for print
  const sorted = [...filtered].sort((a, b) =>
    (a.nome_responsavel || '').localeCompare(b.nome_responsavel || '', 'pt-BR')
  );

  const stats = {
    total: sorted.length,
    aprovados: sorted.filter(p => p.status_pagamento === 'aprovado').length,
    pendentes: sorted.filter(p => p.status_pagamento === 'pendente').length,
    checkinFeito: sorted.filter(p => p.status_checkin === 'realizado' || p.status_checkin === 'presente').length,
    valorTotal: sorted.reduce((sum, p) => sum + (Number(p.valor_pago) || 0), 0),
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  const statusLabel = (s) => ({
    aprovado: 'APROVADO',
    pendente: 'PENDENTE',
    rejeitado: 'REJEITADO',
    reembolsado: 'REEMBOLSADO',
    cancelado: 'CANCELADO',
    cortesia: 'CORTESIA',
  }[s] || (s || '').toUpperCase());

  const geradoEm = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="print-page bg-white text-black min-h-screen">
      {/* Toolbar - hidden on print */}
      <div className="no-print sticky top-0 bg-gray-900 text-white p-4 flex items-center justify-between shadow-lg z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/participantes')}
            className="flex items-center gap-2 px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
            data-testid="voltar-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
          <span className="text-sm text-gray-300">
            Relação de Inscritos — {sorted.length} registros
          </span>
        </div>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold"
          data-testid="imprimir-btn"
        >
          <Printer className="w-4 h-4" />
          Imprimir / Salvar PDF
        </button>
      </div>

      {/* Print content */}
      <div className="p-8 max-w-full">
        {/* Header */}
        <div className="border-b-2 border-orange-500 pb-4 mb-4">
          <h1 className="text-2xl font-bold mb-1">Kairós — Encontro de Casais</h1>
          <h2 className="text-lg text-gray-700">Relação Completa de Inscritos</h2>
          <p className="text-xs text-gray-500 mt-2">Documento gerado em: {geradoEm}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-2 mb-6 text-xs">
          <div className="border border-gray-300 rounded p-2 text-center">
            <div className="text-gray-500 uppercase">Total</div>
            <div className="text-xl font-bold">{stats.total}</div>
          </div>
          <div className="border border-green-300 bg-green-50 rounded p-2 text-center">
            <div className="text-gray-500 uppercase">Aprovados</div>
            <div className="text-xl font-bold text-green-700">{stats.aprovados}</div>
          </div>
          <div className="border border-yellow-300 bg-yellow-50 rounded p-2 text-center">
            <div className="text-gray-500 uppercase">Pendentes</div>
            <div className="text-xl font-bold text-yellow-700">{stats.pendentes}</div>
          </div>
          <div className="border border-blue-300 bg-blue-50 rounded p-2 text-center">
            <div className="text-gray-500 uppercase">Check-in Feito</div>
            <div className="text-xl font-bold text-blue-700">{stats.checkinFeito}</div>
          </div>
          <div className="border border-orange-300 bg-orange-50 rounded p-2 text-center">
            <div className="text-gray-500 uppercase">Total Recebido</div>
            <div className="text-sm font-bold text-orange-700">{formatCurrency(stats.valorTotal)}</div>
          </div>
        </div>

        {/* Filters applied */}
        {(filterStatus !== 'all' || filterEvento !== 'all' || filterLote !== 'all' || filterCheckin !== 'all') && (
          <div className="mb-4 text-xs bg-gray-100 border border-gray-300 rounded p-2">
            <strong>Filtros aplicados:</strong>
            {filterStatus !== 'all' && ` Status=${statusLabel(filterStatus)}`}
            {filterEvento !== 'all' && ` | Evento=${getEventoName(filterEvento)}`}
            {filterLote !== 'all' && ` | Lote=${getLoteName(filterLote)}`}
            {filterCheckin !== 'all' && ` | Check-in=${filterCheckin === 'presente' ? 'Presentes' : 'Não Chegaram'}`}
          </div>
        )}

        {/* Table */}
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-800 text-white">
              <th className="border border-gray-400 px-1 py-1 text-left w-6">#</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Responsável</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Cônjuge</th>
              <th className="border border-gray-400 px-1 py-1 text-left">E-mail</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Telefone</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Cidade</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Status</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Código</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Valor</th>
              <th className="border border-gray-400 px-1 py-1 text-left">Inscrição</th>
              <th className="border border-gray-400 px-1 py-1 text-center">Check-in</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, idx) => (
              <tr key={p.participante_id || p.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-1 py-1 text-center">{idx + 1}</td>
                <td className="border border-gray-300 px-1 py-1 font-medium">{p.nome_responsavel || '-'}</td>
                <td className="border border-gray-300 px-1 py-1">{p.nome_conjuge || '-'}</td>
                <td className="border border-gray-300 px-1 py-1 text-[10px]">{p.email || '-'}</td>
                <td className="border border-gray-300 px-1 py-1 text-[10px]">{p.telefone || '-'}</td>
                <td className="border border-gray-300 px-1 py-1">{p.cidade || '-'}</td>
                <td className="border border-gray-300 px-1 py-1">
                  <span className={`px-1 py-0.5 rounded text-[10px] font-bold ${
                    p.status_pagamento === 'aprovado' ? 'bg-green-200 text-green-800' :
                    p.status_pagamento === 'pendente' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-red-200 text-red-800'
                  }`}>
                    {statusLabel(p.status_pagamento)}
                  </span>
                </td>
                <td className="border border-gray-300 px-1 py-1 font-mono text-[10px]">{p.codigo_ingresso || '-'}</td>
                <td className="border border-gray-300 px-1 py-1 text-right">{formatCurrency(p.valor_pago)}</td>
                <td className="border border-gray-300 px-1 py-1 text-[10px]">{formatDate(p.criado_em)}</td>
                <td className="border border-gray-300 px-1 py-1 text-center">
                  {(p.status_checkin === 'realizado' || p.status_checkin === 'presente') ? (
                    <span className="text-green-700 font-bold">✓</span>
                  ) : (
                    <span className="text-gray-400">☐</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sorted.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum participante encontrado com os filtros aplicados.
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-300 text-xs text-gray-500 text-center">
          Kairós - Encontro de Casais • kairosseminarios.com.br • Documento gerado em {geradoEm}
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-page { padding: 0 !important; }
          table { font-size: 9px !important; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

export default ParticipantesImprimirPage;
